import { UserAvatar } from "@/components/common/user-avatar";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { getStatusColor } from "@/pages/orders/utils";
import { IOrderData } from "@/types/order";
import { ColumnDef } from "@tanstack/react-table";
import { FileQuestion, Laptop2, Smartphone } from "lucide-react";
import React from "react";

export const useOrdersColumns = () => {
  const columns = React.useMemo<ColumnDef<IOrderData>[]>(
    () => [
      {
        id: "id",
        accessorKey: "document.id",
        header: "ID",
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      {
        id: "confirmationNumber",
        accessorKey: "document.confirmationNumber",
        header: "Confirmation No.",
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div className="truncate font-mono">{getValue()}</div>,
          );
        },
      },
      {
        id: "email",
        accessorKey: "document.params.email",
        header: "Email",
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        cell: function render({ getValue }: any) {
          const status: string = getValue();
          return (
            <div className="p-2">
              <Badge className={getStatusColor(status)}>{status}</Badge>
            </div>
          );
        },
        enableSorting: false,
        minSize: 175,
        meta: {
          filterType: "select",
          options: [
            { label: "Awaiting Reservation", value: "AwaitingReservation" },
            { label: "Ticket Reserved", value: "TicketsReserved" },
            {
              label: "Ticket Reservation Failed",
              value: "TicketReservationFailed",
            },
            { label: "Payment Completed", value: "PaymentCompleted" },
            { label: "Payment Declined", value: "PaymentDeclined" },
            { label: "Order Cancelled", value: "OrderCancelled" },
            { label: "Refund Processed", value: "RefundProcessed" },
            { label: "Refund Failed", value: "RefundFailed" },
            { label: "Success", value: "Success" },
          ],
        },
      },
      {
        id: "botStatus",
        header: "Bot Status",
        accessorKey: "document.botStatus",
        cell: function render({ getValue }: any) {
          const status = getValue();
          return <Badge className={getStatusColor(status)}>{status}</Badge>;
        },
        enableSorting: false,
        minSize: 175,
        meta: {
          filterType: "select",
          options: [
            { label: "Idle", value: "Idle" },
            { label: "Attempting Reservation", value: "AttemptingReservation" },
            { label: "Awaiting User Payment", value: "AwaitingUserPayment" },
            { label: "Attempting Purchase", value: "AttemptingPurchase" },
            { label: "Purchase Succeeded", value: "PurchaseSucceeded" },
            { label: "User Payment Error", value: "UserPaymentError" },
            { label: "Reservation Error", value: "ReservationError" },
            { label: "Purchase Error", value: "PurchaseError" },
            { label: "Manual Purchase", value: "ManualPurchase" },
          ],
        },
      },
      {
        id: "event",
        accessorKey: "document.metadata.event",
        header: "Event Name",
        minSize: 200,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <a
              href={`/events/edit/${getValue().id}`}
              target="_blank"
              className="text-blue-500 hover:underline truncate"
            >
              {getValue().name}
            </a>,
          );
        },
      },
      {
        id: "products",
        accessorKey: "document.params.products",
        header: "Products",
        cell: function render({ getValue }: any) {
          const products = getValue();
          if (!products) return "-";
          return products
            .map((p: any) => `${p.quantity} x ${p.name}`)
            .join(", ");
        },
        minSize: 120,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "platform",
        accessorKey: "document.platform",
        header: "Platform",
        cell: function render({ getValue }: any) {
          switch (getValue()) {
            case "mobile":
              return <Smartphone />;
            case "web":
              return <Laptop2 />;
            default:
              return <FileQuestion />;
          }
        },
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "amount",
        accessorKey: "document.amount",
        header: "Amount",
        cell: function render({ getValue }: any) {
          return makeTooltip(`€ ${getValue() ? getValue() / 100 : "-"}`);
        },
        size: 80,
      },
      {
        id: "discount",
        accessorKey: "document.discount",
        header: "Discount",
        cell: function render({ getValue }: any) {
          return makeTooltip(`${getValue() ? getValue() * 100 : "0"}%`);
        },
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "serviceFee",
        accessorKey: "document.metadata.serviceFee",
        header: "Service Fee",
        cell: function render({ getValue }: any) {
          return makeTooltip(`€ ${getValue() ?? "-"}`);
        },
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "provider",
        accessorKey: "document.ticketing.provider",
        header: "Provider",
        cell: function render({ getValue }: any) {
          return makeTooltip(`${getValue() ?? "-"}`);
        },
        enableSorting: false,
      },
      {
        id: "createdAt",
        accessorKey: "document.createdAt",
        accessorFn: (row) => parseUnixTimestamp(row.document?.createdAt),
        header: "Created At",
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        meta: {
          filterType: "date",
        },
      },
      {
        id: "updatedAt",
        accessorKey: "document.updatedAt",
        accessorFn: (row) => parseUnixTimestamp(row.document?.updatedAt),
        header: "Updated At",
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
      },
      {
        id: "updatedBy",
        accessorKey: "document",
        header: "Updated By",
        cell: function render({ getValue }: any) {
          const user =
            getValue()?.updatedBy === getValue()?.metadata?.user?.id
              ? getValue().metadata.user
              : null;
          return (
            <div className="p-1">
              {user ? (
                <UserAvatar
                  displayName={user.display_name}
                  photoUrl={user.photo_url}
                  email={user.email}
                  half
                />
              ) : (
                getValue().updatedBy
              )}
            </div>
          );
        },
      },
      {
        id: "createdBy",
        accessorKey: "document.metadata.user",
        header: "Created By",
        cell: function render({ getValue }: any) {
          const user = getValue();
          return (
            <div className="p-1">
              <UserAvatar
                displayName={user.display_name}
                photoUrl={user.photo_url}
                email={user.email}
                half
              />
            </div>
          );
        },
      },
      {
        // Actions column with edit/delete/show buttons
        id: "actions",
        size: 70,
        header: "Actions",
        cell: ({ row }) => (
          <>
            <ShowButton
              size="sm"
              hideText={true}
              recordItemId={row.original.document.id}
            />
          </>
        ),
        enableSorting: false, // Disable sorting for action buttons
        enableColumnFilter: false, // Disable filtering for action buttons
      },
    ],
    [],
  );

  return columns;
};
